import { useState } from "react";
import { ExternalLink, KeyRound, Loader2, Lock, Plug, RefreshCw } from "lucide-react";
import { ConnectionLogo } from "./ConnectionLogo";
import { GuideBlock } from "./GuideBlock";

// The steps a connection can be in once a person has chosen an app. Each is a small view inside the Add panel:
// a sign-in window to wait on, a link that opened, a form that asks for more than a key, an approval to poll.

/* ---------- waiting on another window ---------- */

export function WaitState({ app, label, tone = "busy", title, children, actions, testId, role = "status" }) {
  return (
    <div className={`cx-wait${tone !== "busy" ? ` cx-wait--${tone}` : ""}`} data-testid={testId} role={role}>
      <div className="cx-wait__logo"><span className="cx-wait__ring" aria-hidden="true" /><ConnectionLogo app={app} label={label} size={44} /></div>
      <h3 className="cx-wait__title">{title}</h3>
      <p className="cx-wait__text">{children}</p>
      {actions ? <div className="cx-wait__actions">{actions}</div> : null}
    </div>
  );
}

/**
 * The native sign-in: a popup is open and the page is asking Hermes whether it was approved.
 * flow = { status: "authorization_required" | "approved" | "error" | "conflict", error, authorizationUrl, retryInSeconds }.
 * `onUseKey`, for an app the dashboard also adds by key, opens its key form instead.
 */
export function McpPopupState({ choice, flow, busy = false, onOpenAgain, onRetry, onCancel, onDone, onUseKey = null }) {
  const { label, app } = choice;
  const useKey = onUseKey ? <button type="button" className="cx-btn cx-btn--ghost" data-testid="agent-connections-mcp-use-key" onClick={onUseKey}><KeyRound size={14} />Use a key instead</button> : null;
  if (flow.status === "approved") {
    return (
      <WaitState app={app} label={label} tone="good" title={`${label} is connected`} testId="agent-connections-mcp-popup"
        actions={<button type="button" className="cx-btn cx-btn--primary" onClick={onDone}>Done</button>}>
        Your agent signed in. {Number.isFinite(flow.toolCount) ? <><b>{flow.toolCount} tools</b> came with it.</> : "It can use it now."}
      </WaitState>
    );
  }
  if (flow.status === "conflict") {
    const wait = Math.max(0, Math.ceil(flow.retryInSeconds ?? 0));
    return (
      <WaitState app={app} label={label} tone="warn" title="A sign-in is already in progress" testId="agent-connections-mcp-popup" role="alert"
        actions={<>
          <button type="button" className="cx-btn cx-btn--primary" data-testid="agent-connections-mcp-retry" disabled={busy || wait > 0} onClick={onRetry}>{busy ? <Loader2 size={14} className="cx-spin" /> : <RefreshCw size={14} />}{wait > 0 ? `Retry in ${wait}s` : "Retry"}</button>
          {useKey}
          <button type="button" className="cx-btn" onClick={onCancel}>Cancel</button>
        </>}>
        Finish the sign-in to {label} in the other window, or retry in a minute.
      </WaitState>
    );
  }
  if (flow.status === "error") {
    return (
      <WaitState app={app} label={label} tone="bad" title={`${label} did not connect`} testId="agent-connections-mcp-popup" role="alert"
        actions={<>
          <button type="button" className="cx-btn cx-btn--primary" data-testid="agent-connections-mcp-retry" disabled={busy} onClick={onRetry}>{busy ? <Loader2 size={14} className="cx-spin" /> : <RefreshCw size={14} />}Try again</button>
          {useKey}
          <button type="button" className="cx-btn" onClick={onCancel}>Cancel</button>
        </>}>
        {flow.error || "The sign-in did not finish."}
      </WaitState>
    );
  }
  return (
    <WaitState app={app} label={label} title="Sign-in window open" testId="agent-connections-mcp-popup"
      actions={<>
        {flow.authorizationUrl ? <a className="cx-btn" href={flow.authorizationUrl} target="_blank" rel="noopener noreferrer" onClick={onOpenAgain}><ExternalLink size={14} />Open it again</a> : null}
        {useKey}
        <button type="button" className="cx-btn" onClick={onCancel}>Cancel</button>
      </>}>
      Sign in to <b>{label}</b> in the window that opened. Your agent keeps the sign-in; this page updates by itself.
    </WaitState>
  );
}

/** A Pipedream Connect link opened in a popup; the page refreshes when it closes. */
export function PipedreamLinkState({ choice, url, onOpenAgain, onDone, onCancel }) {
  return (
    <WaitState app={choice.app} label={choice.label} title="Link window open" testId="agent-connections-pipedream-link"
      actions={<>
        {url ? <a className="cx-btn" href={url} target="_blank" rel="noopener noreferrer" onClick={onOpenAgain}><ExternalLink size={14} />Open it again</a> : null}
        <button type="button" className="cx-btn cx-btn--primary" onClick={onDone}>I finished</button>
        <button type="button" className="cx-btn" onClick={onCancel}>Cancel</button>
      </>}>
      Pick the <b>{choice.label}</b> account your agent should use in the window that opened. Close it when you are done.
    </WaitState>
  );
}

/* ---------- approvals the page polls ---------- */

// Higgsfield signs in with a link the page waits on. The person approves it in a new tab while this page keeps
// asking whether they have, so it opens as a real link instead of sending the browser away.
export function HiggsfieldApproval({ step, busy = false, onRestart, onCancel }) {
  if (step.expired) {
    return (
      <WaitState app="higgsfield" label="Higgsfield" tone="bad" title="That Higgsfield link expired" testId="agent-connections-higgsfield-approval" role="alert"
        actions={<>
          <button type="button" className="cx-btn cx-btn--primary" data-testid="agent-connections-higgsfield-restart" disabled={busy} onClick={onRestart}>{busy ? <Loader2 size={14} className="cx-spin" /> : <RefreshCw size={14} />}Start again</button>
          <button type="button" className="cx-btn" data-testid="agent-connections-higgsfield-cancel" onClick={onCancel}>Cancel</button>
        </>}>
        Start again and approve it within a few minutes.
      </WaitState>
    );
  }
  return (
    <WaitState app="higgsfield" label="Higgsfield" title="Waiting for your approval" testId="agent-connections-higgsfield-approval"
      actions={<>
        <a className="cx-btn cx-btn--primary" data-testid="agent-connections-higgsfield-open" href={step.authorizationUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} />Open Higgsfield</a>
        <button type="button" className="cx-btn" data-testid="agent-connections-higgsfield-cancel" onClick={onCancel}>Cancel</button>
      </>}>
      Approve the connection in Higgsfield, then come back here. This page notices by itself.
    </WaitState>
  );
}

export function ShopifyApproval({ step, status = "pending", busy = false, onRestart, onBack }) {
  const text = status === "installing" ? "Shopify approved. Installing and checking access on your agent…"
    : status === "failed" ? "Setup needs attention. See the message above."
    : <>Approve GrowthOS for <b>{step.store}</b>, then return here. Your agent will use the access you approve.</>;
  const tone = status === "failed" ? "bad" : "busy";
  return (
    <WaitState app="shopify" label="Shopify" tone={tone} title={status === "installing" ? "Installing on your agent" : "Waiting for your Shopify approval"} testId="agent-connections-shopify-approval"
      actions={<>
        {status === "pending" ? <a className="cx-btn cx-btn--primary" data-testid="agent-connections-shopify-open" href={step.authorizationUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} />Continue to Shopify</a> : null}
        {status === "failed" ? <button type="button" className="cx-btn cx-btn--primary" disabled={busy} onClick={onRestart}>Start again</button> : null}
        <button type="button" className="cx-btn" onClick={onBack}>{status === "installing" ? "Close" : "Back"}</button>
      </>}>
      {text}
    </WaitState>
  );
}

/* ---------- forms that ask for more than a key ---------- */

export function AmazonGuide({ clientName, busy = false, onContinue, onCancel }) {
  return (
    <div className="cx-form" data-testid="amazon-account-guide">
      <GuideBlock guide={{
        title: "Connect the right Amazon seller account",
        subtitle: "Amazon may use the account already signed in to this browser.",
        steps: [
          `Open Seller Central and sign in to the seller account for ${clientName || "this brand"}.`,
          "If you manage multiple businesses, use the email that belongs only to this seller account, or select the correct business in Seller Central.",
          "Return here and continue. Check the seller name before approving access.",
        ],
        links: [{ label: "Open Seller Central", url: "https://sellercentral.amazon.com/" }],
        reminder: "Still getting the wrong account? Open the Hub in a private browser window and connect from there with the correct Amazon login.",
      }} />
      <div className="cx-form__actions">
        <button type="button" className="cx-btn cx-btn--primary" data-testid="amazon-account-continue" disabled={busy} onClick={onContinue}>{busy ? <Loader2 size={14} className="cx-spin" /> : <Plug size={14} />}Continue to Amazon</button>
        <button type="button" className="cx-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

const GOOGLE_ADS_CUSTOMER_PATTERN = /^\d{3}-?\d{3}-?\d{4}$/;

// Google Ads is the one sign-in the provider will not start without knowing which advertiser account it is for.
export function GoogleAdsCustomerForm({ onSubmit, onCancel, busy = false }) {
  const [customerId, setCustomerId] = useState("");
  const [error, setError] = useState("");
  const submit = (event) => {
    event.preventDefault();
    if (!GOOGLE_ADS_CUSTOMER_PATTERN.test(customerId)) { setError("Enter the 10-digit customer id from Google Ads."); return; }
    setError("");
    onSubmit(customerId.replace(/-/g, ""));
  };
  return (
    <form className="cx-form" onSubmit={submit} autoComplete="off">
      <label className="cx-field">
        <span>Google Ads customer id</span>
        <input type="text" data-testid="agent-connection-google-ads-customer" placeholder="123-456-7890" value={customerId} onChange={(event) => setCustomerId(event.target.value.trim())} autoComplete="off" spellCheck={false} inputMode="numeric" />
        {error ? <em>{error}</em> : null}
      </label>
      <p className="cx-form__note">Find it at the top right of Google Ads. Use the account the agent should work in, not the manager account.</p>
      <div className="cx-form__actions">
        <button type="submit" className="cx-btn cx-btn--primary" data-testid="agent-connection-form-submit" disabled={busy}>{busy ? <Loader2 size={14} className="cx-spin" /> : <Plug size={14} />}Continue</button>
        <button type="button" className="cx-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

const SHOPIFY_DOMAIN_PATTERN = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;
const SHOPIFY_CODE_PATTERN = /^[0-9]{4,8}$/;
const fieldByKey = (app, key) => (app.fields || []).find((field) => (field.envKey || field.name) === key) || {};

export function normalizeShopifyDomain(raw) {
  return String(raw || "").trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/+$/, "");
}

export function ShopifyPartnerForm({ app, onSubmit, onCancel, busy = false }) {
  const storeField = fieldByKey(app, "SHOPIFY_STORE_DOMAIN");
  const codeField = fieldByKey(app, "SHOPIFY_PARTNER_CODE");
  const [store, setStore] = useState("");
  const [code, setCode] = useState("");
  const [errors, setErrors] = useState({});
  const submit = (event) => {
    event.preventDefault();
    const next = {};
    const normalizedStore = normalizeShopifyDomain(store);
    if (!SHOPIFY_DOMAIN_PATTERN.test(normalizedStore)) next.store = "Enter the permanent .myshopify.com URL.";
    const normalizedCode = code.replace(/\s+/g, "");
    if (!SHOPIFY_CODE_PATTERN.test(normalizedCode)) next.code = "Enter the collaborator request code (numbers only).";
    setErrors(next);
    if (Object.keys(next).length) return;
    onSubmit(app.app, { SHOPIFY_STORE_DOMAIN: normalizedStore, SHOPIFY_PARTNER_CODE: normalizedCode });
  };
  return (
    <form className="cx-form" onSubmit={submit} autoComplete="off">
      <GuideBlock guide={app.guide} />
      <label className="cx-field">
        <span>{storeField.label || "Permanent .myshopify.com URL"}</span>
        <input type="text" data-testid="agent-connection-shopify-store" placeholder={storeField.placeholder} value={store} onChange={(event) => setStore(event.target.value)} autoComplete="off" spellCheck={false} />
        {errors.store ? <em>{errors.store}</em> : null}
      </label>
      <label className="cx-field">
        <span>{codeField.label || "Shopify Partner code"}</span>
        <input type="password" data-testid="agent-connection-shopify-code" placeholder={codeField.placeholder} value={code} onChange={(event) => setCode(event.target.value)} autoComplete="off" />
        {errors.code ? <em>{errors.code}</em> : null}
      </label>
      <div className="cx-form__actions">
        <button type="submit" className="cx-btn cx-btn--primary" data-testid="agent-connection-form-submit" disabled={busy}>{busy ? <Loader2 size={14} className="cx-spin" /> : <Lock size={14} />}Submit to X SCALE</button>
        <button type="button" className="cx-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
